import {
  HeaderLiteMenuItem,
  HeaderLiteSettings,
} from "components/HeaderNew/HeaderLite/types";
import {
  HeaderLiteSettingsSchema,
  HeaderMenuLiteItemSchema,
} from "components/HeaderNew/HeaderLite/CfHeaderLite/schema";
import {
  HeaderMenuItem,
  HeaderSettings,
  HeaderSubmenuGroup,
  HeaderSubmenuItem,
  HeaderSubmenuSidebar,
} from "components/HeaderNew/HeaderFull/types";
import {
  HeaderMenuItemSchema,
  HeaderMenuSettingsSchema,
  HeaderSubmenuGroupSchema,
  HeaderSubmenuSchema,
  HeaderSubmenuSidebarSchema,
} from "components/HeaderNew/HeaderFull/CfHeaderMenuFull/schema";
import { convertJsonAnchorToPolyAnchor } from "components/Anchor/utils";
import Image from "components/Image";
import React from "react";

const convertSubmenuGroup = (
  group: HeaderSubmenuGroupSchema[],
): HeaderSubmenuGroup[] =>
  group.map(({ title, items }) => ({
    title,
    items: items.map(
      ({ alt, blank, href, dataTracking, description, ...item }) => ({
        ...item,
        description: description || undefined,
        alt: alt || "",
        anchorProps: convertJsonAnchorToPolyAnchor({
          href,
          blank,
          dataTracking,
        }),
      }),
    ),
  }));

const convertSubmenuSidebar = (
  sidebar: HeaderSubmenuSidebarSchema | null,
): HeaderSubmenuSidebar | null => {
  if (!sidebar) {
    return null;
  }

  const { ctaImage, image, ...otherSidebarProps } = sidebar;
  const imageProps = image && { imageDesktop: image.src, alt: image.alt };

  return {
    ctaImage: ctaImage ? convertJsonAnchorToPolyAnchor(ctaImage) : ctaImage,
    image: imageProps && <Image {...imageProps} />,
    ...otherSidebarProps,
  };
};

export const convertSubmenu = (
  submenu: HeaderSubmenuSchema[],
): HeaderSubmenuItem[] =>
  submenu.map(
    ({ label, sidebarMenu, groups, showAllCtaLabel, ...anchorProps }) => ({
      label,
      sidebarMenu: convertSubmenuSidebar(sidebarMenu),
      groups: convertSubmenuGroup(groups),
      anchorProps: convertJsonAnchorToPolyAnchor(anchorProps),
      showAllCtaLabel: showAllCtaLabel || undefined,
    }),
  );

export const convertMenu = (menu: HeaderMenuItemSchema[]): HeaderMenuItem[] =>
  menu.map(({ label, submenu, ...anchorProps }) => ({
    label,
    submenu: convertSubmenu(submenu),
    anchorProps: convertJsonAnchorToPolyAnchor(anchorProps),
  }));

export const convertMenuLite = (
  menu: HeaderMenuLiteItemSchema[],
): HeaderLiteMenuItem[] =>
  menu.map(({ label, ...anchorProps }) => ({
    label,
    anchorProps: convertJsonAnchorToPolyAnchor(anchorProps),
  }));

export const convertSettings = ({
  logo: { image, ctaAnchor },
  privateArea,
  ...otherSettings
}: HeaderMenuSettingsSchema): HeaderSettings => {
  const imageProps = { imageDesktop: image.src, alt: image.alt };
  return {
    logo: {
      image: <Image {...imageProps} />,
      ctaAnchor: convertJsonAnchorToPolyAnchor(ctaAnchor),
    },
    privateArea: privateArea.map(
      ({ ctaAnchor: privateAreaCtaAnchor, ...otherPrivateAreaSettings }) => ({
        ctaAnchor: convertJsonAnchorToPolyAnchor(privateAreaCtaAnchor),
        ...otherPrivateAreaSettings,
      }),
    ),
    ...otherSettings,
  };
};

export const convertSettingsLite = ({
  logo: { image, ctaAnchor },
  ...otherSettings
}: HeaderLiteSettingsSchema): HeaderLiteSettings => {
  const imageProps = { imageDesktop: image.src, alt: image.alt };

  return {
    logo: {
      image: <Image {...imageProps} />,
      ...(ctaAnchor && { ctaAnchor: convertJsonAnchorToPolyAnchor(ctaAnchor) }),
    },
    ...otherSettings,
  };
};
